import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { AutoDimensionHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocEntityHandle, GroupHandle } from "building-types/handles/docEntityHandleTypes";
import { assert, assertIsExactly } from "common/utils/assertOfType";
import type { DocEntityNode } from "./docEntityNode";

export function isDocEntityNodeOfType<T extends DocEntityType>(node: DocEntityNode, type: T): node is DocEntityNode<T> {
  return node.getEntityType() === type;
}

export function isDocEntityBase<T extends DocEntityType>(entity: unknown): entity is DocEntityBase<T> {
  return typeof entity === "object" && entity !== null && "id" in entity && "name" in entity && "entityType" in entity;
}

export function assertIsDocEntityBase(entity: unknown): asserts entity is DocEntityBase {
  assert(isDocEntityBase(entity));
}

export function isDocEntityOfType<T extends DocEntityType>(
  entity: unknown,
  entityType: T,
): entity is DocEntityTypeToValueType[T] {
  return isDocEntityBase(entity) && entity.entityType === entityType;
}

/**
 * Asserts that the given DocEntityNode is of the specified entity type.
 * @param node - The DocEntityNode to check
 * @param type - The expected DocEntityType
 * @throws {TypeError} If the node is not of the specified type
 */
export function assertIsDocEntityNodeOfType<T extends DocEntityType>(
  node: DocEntityNode,
  type: T,
): asserts node is DocEntityNode<T> {
  assert(isDocEntityNodeOfType(node, type));
  assertIsExactly(node.getEntityType(), type);
}

export function assertIsDocEntityOfType<T extends DocEntityType>(
  entity: unknown,
  entityType: T,
): asserts entity is DocEntityTypeToValueType[T] {
  assertIsDocEntityBase(entity);
  assertIsExactly(entity.entityType, entityType);
}

export function ensureDocEntityType<T extends DocEntityType>(
  entity: DocEntityBase,
  entityType: T,
): DocEntityTypeToValueType[T] {
  assertIsDocEntityOfType(entity, entityType);
  return entity;
}

export const docEntityBaseFields: (keyof DocEntityBase<DocEntityType>)[] = ["id", "name", "entityType", "sortIndex"];

/** Doc entity keys stripped by {@link stripDocEntityBaseFields}; `name` is retained. */
export type DocEntityManagedFieldKeys = "id" | "entityType" | "sortIndex" | "parentId";

export function stripDocEntityBaseFields<T extends DocEntityBase>(entity: T): Omit<T, DocEntityManagedFieldKeys> {
  const { id, entityType, sortIndex, parentId, ...rest } = entity;
  void id;
  void entityType;
  void sortIndex;
  void parentId;
  return rest;
}

export function isGroupHandle(x: DocEntityHandle): x is GroupHandle {
  return x.type === "GROUP";
}

export function isAutoDimensionHandle(x: DocEntityHandle): x is AutoDimensionHandle {
  return x.type === "AUTO_DIMENSION";
}
